import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const buttonVariants = cva(
	'focus-cosy text-body-xs inline-flex h-10 items-center justify-center gap-2 rounded-xl px-3 font-medium transition disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				// `data-preview="hover"` holds the hover look on a resting button,
				// for the styleguide's states row.
				default:
					'bg-gradient-brand text-brand-foreground shadow-brand hover:shadow-brand-lg data-[preview=hover]:shadow-brand-lg hover:-translate-y-px hover:brightness-[1.06] data-[preview=hover]:-translate-y-px data-[preview=hover]:brightness-[1.06] motion-reduce:hover:translate-y-0',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline:
					'border-input bg-control-glass text-foreground hover:bg-accent hover:text-accent-foreground border',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				white:
					'border-primary bg-background text-primary hover:bg-background/90 border',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-10 px-3',
				wide: 'h-12 px-10 md:px-24',
				sm: 'h-9 px-2',
				lg: 'h-11 px-6',
				pill: 'rounded-full px-4',
				icon: 'size-10',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariant = VariantProps<typeof buttonVariants>

const Button = ({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	ButtonVariant & {
		asChild?: boolean
	}) => {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	)
}

export { Button, buttonVariants }
